import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { getOrgTermsContent } from '../../../content/utils.js';
import SubHeader from '../../SubHeader.jsx';

ReportResultsOverview.propTypes = {
  projectsCount: PropTypes.number.isRequired,
  resultsGroups: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      projects: PropTypes.array.isRequired,
    })
  ).isRequired,
};

const COLORS = [
  '#27B0E0',
  '#82C2A5',
  '#F7A021',
  '#C35D5D',
  '#9E6DC4',
  '#C0D081',
  '#8DABC1',
  '#F3CA39',
  '#F18A8A',
  '#5D6995',
  '#E2BA52',
  '#BFA1D7',
  '#95BC77',
  '#E06F6C',
  '#6F97D4',
  '#F17AA5',
  '#7198FF',
  '#B6E370',
  '#3CB0C9',
  '#F8A488',
  '#7E75E8',
  '#CF505C',
  '#64B49C',
  '#5771CA',
  '#DC64A2',
  '#67DACC',
  '#D7E250',
  '#33A97E',
  '#DA4A7D',
  '#C28D66',
];

const RADIAN = Math.PI / 180;

const textProps = {
  fill: 'var(--color--dark-blue-grey)',
  fontFamily: 'Varela Round',
  textAnchor: 'middle',
};

const renderLabel = (props) => {
  const {
    cx,
    cy,
    fill,
    name,
    midAngle,
    outerRadius,
    percent,
    shownValues,
    values,
    handleUpdateLabels,
  } = props;

  if (!values[name]) {
    handleUpdateLabels({
      key: name,
      showValue: Math.floor(percent * 100),
      value: percent * 100,
    });
  }

  if (percent < 0.01) {
    return null;
  }

  const isNumberBetween = (number, min, max) => {
    return number >= min && number <= max;
  };

  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);

  let offsetRadius = 0;

  if (isNumberBetween(midAngle, 0, 90)) {
    offsetRadius += midAngle;
  }

  if (isNumberBetween(midAngle, -90, 0)) {
    offsetRadius += Math.abs(midAngle);
  }

  if (isNumberBetween(midAngle, -180, -91)) {
    offsetRadius += 90 - (Math.abs(midAngle) - 90);
  }

  if (isNumberBetween(midAngle, -270, -181)) {
    offsetRadius += Math.abs(midAngle) - 180;
  }

  const sx = cx + (outerRadius + 4) * cos;
  const sy = cy + (outerRadius + 4) * sin;
  const mx = cx + (outerRadius + offsetRadius) * cos;
  const my = cy + (outerRadius + offsetRadius) * sin;
  const ex = cx + (50 + outerRadius) * (cos >= 0 ? 1 : -1);
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <path
        className="test"
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        fill="none"
        stroke={fill}
      />
      <circle cx={ex} cy={ey} r={4} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 10}
        y={ey}
        fill="var(--color--dark-blue-grey)"
        textAnchor={textAnchor}
        dominantBaseline="central"
      >
        {name} &bull;
        {shownValues && shownValues[name]
          ? `${shownValues[name].showValue}%`
          : ''}
      </text>
    </g>
  );
};

function ReportResultsOverview({ projectsCount, resultsGroups, pieRef }) {
  const [values, setValues] = useState({});
  const [shownValues, setShownValues] = useState({});
  const data = resultsGroups.map(({ title, projects }) => {
    return {
      name: title,
      value: projects.length,
    };
  });
  const handleUpdateLabels = (value) => {
    setValues((prev) => ({ ...prev, [value.key]: value }));
  };

  const height = data.length / 0.2 + 400;
  const shouldAlternateLastColor = data.length % COLORS.length === 1;

  useEffect(() => {
    let totalPercents = 0;
    const priority = [];
    let sum = 0;
    if (Object.keys(values).length === data.length) {
      Object.keys(values).forEach((key) => {
        totalPercents += Math.floor(values[key].value);
        sum += values[key].value;
        priority.push({
          name: key,
          value: values[key].value,
          shown: values[key].showValue,
          diff: values[key].value - values[key].showValue,
        });
      });

      const diff = 100 - totalPercents;
      const numberBiggerPercents = Array.from({ length: diff });
      const sortedPriority = priority.sort((a, b) => b.diff - a.diff);
      const updatedValues = { ...values };

      numberBiggerPercents.forEach((v, index) => {
        if (sortedPriority[index] && sortedPriority[index].name) {
          updatedValues[sortedPriority[index].name].showValue =
            updatedValues[sortedPriority[index].name].showValue + 1;
        }
      });
      setShownValues(updatedValues);
    }
  }, [values]);

  useEffect(() => {
    setValues({});
  }, [resultsGroups]);

  return (
    <React.Fragment>
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Pie Chart</h2>
        </div>
      </SubHeader>
      <div ref={pieRef} className="component component-bg-white">
        <ResponsiveContainer height={height}>
          <PieChart>
            <text {...textProps} x="50%" y="50%" fontSize={28}>
              {projectsCount}
            </text>
            <text {...textProps} x="50%" y="50%" dy="20">
              {pluralize(getOrgTermsContent('project'), projectsCount)}
            </text>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={72}
              outerRadius={108}
              isAnimationActive={false}
              startAngle={90}
              endAngle={-270}
              label={(props) =>
                renderLabel({
                  ...props,
                  handleUpdateLabels,
                  values,
                  shownValues,
                })
              }
              labelLine={false}
            >
              {data.map((entry, index) => {
                const isLastItem = index === data.length - 1;
                return (
                  <Cell
                    key={index}
                    fill={
                      shouldAlternateLastColor && isLastItem
                        ? COLORS[2]
                        : COLORS[index % COLORS.length]
                    }
                    stroke="none"
                  />
                );
              })}
            </Pie>
            <Tooltip
              contentStyle={{
                background: 'var(--color--light-pale-grey)',
                borderColor: 'var(--color--silver)',
                borderRadius: 4,
                padding: '4px 10px',
              }}
              itemStyle={{ color: 'var(--color--dark-blue-grey)' }}
              separator=" &bull; "
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </React.Fragment>
  );
}

export default ReportResultsOverview;
